import { BaseSyntheticEvent, ReactElement, RefObject, startTransition } from "react"

import { useClickAway, useKeyPressEvent, useToggle } from "react-use"

import { CrossIcon } from "@/3514/assets"
import { Avatar } from "@/3514/components/shared/avatar/avatar.component"
import { EAvatarSize } from "@/3514/components/shared/avatar/avatar.types"
import styles from "@/3514/components/shared/headers/headers.styles"
import { Link } from "@/3514/components/shared/link/link.component"
import {
    ETypographyColor,
    ETypographySize,
    ETypographyWeight
} from "@/3514/components/shared/typography/typography.types"
import { EPath } from "@/3514/constants"
import {
    type TLogoutFunction,
    type TScreenBreakpoints,
    type TUserState,
    useLogout,
    useScreenBreakpoints,
    useUser
} from "@/3514/hooks"
import { EUserRole } from "@/3514/types"
import { isUserCoach, withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"
import { HeaderDropdownProfileList } from "@/shared/header-dropdown-profile-list"
import type { TEmptyAsyncCallback } from "@/shared/types/functions"

type TProfileProps<TRole> = {
    role: TRole
    dropdownRef: RefObject<HTMLDivElement>
    onLogoutComplete?: TEmptyAsyncCallback
}
function HeaderProfile<TRole extends EUserRole = EUserRole>({
    role,
    onLogoutComplete,
    dropdownRef
}: TProfileProps<TRole>): ReactElement {
    const { user }: TUserState<TRole> = useUser<TRole>()

    const logout: TLogoutFunction = useLogout()

    const { t }: IUseTranslation = useTranslation()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const [isDropdownOpen, setIsDropdownOpen]: ReturnType<typeof useToggle> = useToggle(false)

    useClickAway(dropdownRef, (): void => startTransition((): void => setIsDropdownOpen(false)))

    useKeyPressEvent("Escape", (): void => startTransition((): void => setIsDropdownOpen(false)))

    const handleMenuOpen: ReturnType<typeof withPressHandler> = withPressHandler((): void =>
        startTransition((): void => setIsDropdownOpen(true))
    )

    const handleMenuClose: ReturnType<typeof withPressHandler> = withPressHandler((): void =>
        startTransition((): void => setIsDropdownOpen(false))
    )

    async function handleLogout(e: BaseSyntheticEvent): Promise<void> {
        e.preventDefault()

        handleMenuOpen(e)

        await logout({
            onComplete: onLogoutComplete
        })
    }

    // @ts-expect-error user role ts error can be ignored
    const classes = styles[role]

    return (
        <div className={classes.profileBlock.container({ isDropdownOpen })}>
            <div
                role="button"
                tabIndex={0}
                aria-expanded={isDropdownOpen}
                onClick={withPressHandler(handleMenuOpen)}
                onKeyDown={withPressHandler(handleMenuOpen)}
                className={classes.profileBlock.opener({ isDropdownOpen, isMobile: breakpoints.xs || breakpoints.sm })}
            >
                <Avatar
                    {...((role === EUserRole.Coach || role === EUserRole.Manager) && { size: EAvatarSize._30 })}
                    isCurrentUser
                    isAccessible
                    isLettered
                />
            </div>

            <div
                className={classes.profileBlock.list({
                    isOpen: isDropdownOpen,
                    isMobile: breakpoints.xs || breakpoints.sm
                })}
                ref={dropdownRef}
            >
                {(breakpoints.xs || breakpoints.sm) && (
                    <div
                        tabIndex={0}
                        role="button"
                        className={classes.profileBlock.mobileListCloser()}
                        onClick={withPressHandler(handleMenuClose)}
                        onKeyDown={withPressHandler(handleMenuClose)}
                    >
                        <CrossIcon width={30} height={30} />
                    </div>
                )}

                {(breakpoints.xs || breakpoints.sm) && role === EUserRole.Coach && (
                    <Link
                        to={EPath.Coach_Dashboard({ tab: "Sessions" })}
                        className={classes.profileBlock.listItem()}
                        styleless
                        onClick={handleMenuClose}
                        onKeyDown={handleMenuClose}
                        textWeight={ETypographyWeight.Default}
                        textColor={ETypographyColor.Black}
                        textSize={
                            breakpoints.xs || breakpoints.sm ? ETypographySize.MediumPlus : ETypographySize.Medium
                        }
                    >
                        {t("common.dashboard")}
                    </Link>
                )}

                {(breakpoints.xs || breakpoints.sm) && role === EUserRole.Coach && (
                    <Link
                        to={EPath.Coach_CalendarAvailability()}
                        className={classes.profileBlock.listItem()}
                        styleless
                        onClick={handleMenuClose}
                        onKeyDown={handleMenuClose}
                        textWeight={ETypographyWeight.Default}
                        textColor={ETypographyColor.Black}
                        textSize={
                            breakpoints.xs || breakpoints.sm ? ETypographySize.MediumPlus : ETypographySize.Medium
                        }
                    >
                        {t("coachSide.header.nav.calendarAvailability")}
                    </Link>
                )}

                {(breakpoints.xs || breakpoints.sm) && role === EUserRole.Coach && (
                    <Link
                        to={EPath.Coach_CalendarSetup()}
                        className={classes.profileBlock.listItem()}
                        styleless
                        onClick={handleMenuClose}
                        onKeyDown={handleMenuClose}
                        textWeight={ETypographyWeight.Default}
                        textColor={ETypographyColor.Black}
                        textSize={
                            breakpoints.xs || breakpoints.sm ? ETypographySize.MediumPlus : ETypographySize.Medium
                        }
                    >
                        {t("coachSide.header.nav.calendarSetup")}
                    </Link>
                )}

                {(breakpoints.xs || breakpoints.sm) && role === EUserRole.Coach && (
                    <Link
                        to={EPath.Coach_CalendarAccounts()}
                        className={classes.profileBlock.listItem()}
                        styleless
                        onClick={handleMenuClose}
                        onKeyDown={handleMenuClose}
                        textWeight={ETypographyWeight.Default}
                        textColor={ETypographyColor.Black}
                        textSize={
                            breakpoints.xs || breakpoints.sm ? ETypographySize.MediumPlus : ETypographySize.Medium
                        }
                    >
                        {t("coachSide.header.nav.calendarAccounts")}
                    </Link>
                )}

                <Link
                    to="#"
                    className={classes.profileBlock.listItem()}
                    styleless
                    textWeight={ETypographyWeight.Default}
                    textColor={ETypographyColor.Black}
                    textSize={breakpoints.xs || breakpoints.sm ? ETypographySize.MediumPlus : ETypographySize.Medium}
                    onClick={withPressHandler(handleLogout)}
                    onKeyDown={withPressHandler(handleLogout)}
                >
                    {t("common.signOut")}
                </Link>

                {isUserCoach(user) && user.areSubUsersPresent ? (
                    <HeaderDropdownProfileList
                        listClassName={classes.profileBlock.profileList({
                            isMobile: breakpoints.xs || breakpoints.sm
                        })}
                        titleClassName={classes.profileBlock.profileListTitle({
                            isMobile: breakpoints.xs || breakpoints.sm
                        })}
                    />
                ) : null}
            </div>
        </div>
    )
}

export default HeaderProfile
